import { CommentItem, Vendor, VendorRule } from "@finway-group/shared/lib/models"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { VendorService } from "Shared/services"

import { CacheEnum } from "../consts"
import { invalidateVendorLists } from "./vendor.queries"

type UpdateVendorType = {
    vendorId: string
    updateVendorData: Vendor
}

type UpdateVendorRuleType = {
    vendorId: string
    vendorRule: VendorRule
}

type UpdateVendorContractsType = {
    vendorId: string
    vendorContracts: Array<string>
}

type UpdateVendorCommentsType = {
    vendorId: string
    vendorComments: Array<CommentItem>
}

export const useCreateVendor = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (newVendor: Vendor) => VendorService.createOne(newVendor),
        onSuccess: (createdVendor: Vendor) => {
            invalidateVendorLists()
            queryClient.setQueryData([CacheEnum.VENDORS, createdVendor.id], createdVendor)
        },
    })
}

export const useUpdateVendor = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ vendorId, updateVendorData }: UpdateVendorType) => VendorService.updateOne(vendorId, updateVendorData),
        onSuccess: (updatedVendor: Vendor) => {
            invalidateVendorLists()
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}

export const useSoftDeleteVendor = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ vendorId, updateVendorData }: UpdateVendorType) => {
            const vendorData = new Vendor({ ...updateVendorData, deleted: true, dateDeleted: Date.now() })
            const updatedVendor = await VendorService.updateOne(vendorId, vendorData)

            return updatedVendor
        },
        onSuccess: (updatedVendor: Vendor) => {
            invalidateVendorLists()
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}

export const useHardDeleteVendor = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (vendorId: string) => VendorService.deleteOne(vendorId),
        onSuccess: (_, id) => {
            invalidateVendorLists()
            queryClient.removeQueries([CacheEnum.VENDORS, id])
        },
    })
}

export const useApproveVendorStatus = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (vendorId: string) => VendorService.approveVendorStatus(vendorId),
        onSuccess: (updatedVendor: Vendor) => {
            invalidateVendorLists()
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}

export const useRestoreVendor = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ vendorId, updateVendorData }: UpdateVendorType) => {
            const vendorData = new Vendor({ ...updateVendorData, deleted: false, dateDeleted: undefined })
            const restoredVendor = await VendorService.updateOne(vendorId, vendorData)

            return restoredVendor
        },
        onSuccess: (restoredVendor: Vendor) => {
            invalidateVendorLists()
            queryClient.setQueryData([CacheEnum.VENDORS, restoredVendor.id], restoredVendor)
        },
    })
}

export const useUpdateVendorRule = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ vendorId, vendorRule }: UpdateVendorRuleType) => VendorService.updateVendorRule(vendorId, vendorRule),
        onSuccess: (updatedVendor: Vendor) => {
            queryClient.invalidateQueries({ queryKey: [CacheEnum.VENDORS_LIST] })
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}

export const useUpdateVendorContracts = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ vendorId, vendorContracts }: UpdateVendorContractsType) => VendorService.updateVendorContracts(vendorId, vendorContracts),
        onSuccess: (updatedVendor: Vendor) => {
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}

export const useUpdateVendorComments = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({ vendorId, vendorComments }: UpdateVendorCommentsType) => VendorService.updateComment(vendorId, vendorComments),
        onSuccess: (updatedVendor: Vendor) => {
            queryClient.setQueryData([CacheEnum.VENDORS, updatedVendor.id], updatedVendor)
        },
    })
}
